import { Capacitor } from '@capacitor/core';

const APP_URL_SCHEME = 'com.dice1k.app';
const RAW_PUBLIC_WEB_URL = import.meta.env?.VITE_PUBLIC_WEB_URL;
const DEFAULT_PUBLIC_WEB_ORIGIN = 'https://dice1k.netlify.app';

const looksLikeMissingEnvPlaceholder = (raw?: string): boolean => {
  if (!raw) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized.startsWith('no value set ') || normalized.startsWith('no project id found');
};

const toOrigin = (raw?: string): string => {
  if (!raw) return '';
  if (looksLikeMissingEnvPlaceholder(raw)) return '';
  try {
    return new URL(raw).origin;
  } catch {
    return '';
  }
};

const PUBLIC_WEB_ORIGIN = toOrigin(RAW_PUBLIC_WEB_URL);

export const isNativeApp = (): boolean => Capacitor.isNativePlatform?.() ?? false;

export const buildNativeAppUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${APP_URL_SCHEME}://${normalizedPath}`;
};

export const resolvePublicWebOrigin = (): string => {
  if (PUBLIC_WEB_ORIGIN) return PUBLIC_WEB_ORIGIN;
  return isNativeApp() ? DEFAULT_PUBLIC_WEB_ORIGIN : '';
};

const normalizeIncomingUrl = (rawUrl: string): string | null => {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('intent://')) {
    const match = rawUrl.match(/^intent:\/\/([^#]+)#Intent;scheme=([^;]+);/);
    if (match) {
      return `${match[2]}://${match[1]}`;
    }
  }
  if (rawUrl.startsWith('android-app://')) {
    const trimmed = rawUrl.replace('android-app://', '');
    const parts = trimmed.split('/');
    if (parts.length >= 3) {
      const scheme = parts[1];
      const host = parts[2];
      const path = parts.slice(3).join('/');
      return `${scheme}://${host}/${path}`;
    }
  }
  return rawUrl;
};

export const buildOnlineShareLink = (groupToken?: string | null, gameId?: string | null): string => {
  const params = new URLSearchParams();
  if (groupToken) {
    params.set('group', groupToken);
    if (gameId) {
      params.set('game', gameId);
    }
  } else if (gameId) {
    params.set('game', gameId);
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : '';
  const origin = resolvePublicWebOrigin() || (typeof window !== 'undefined' ? window.location.origin : '');
  const normalized = origin.replace(/\/+$/, '');
  if (!normalized) return `/online${suffix}`;
  return `${normalized}/online${suffix}`;
};

export const buildOnlineInvitePath = (groupToken?: string | null, inviteId?: string | null, gameId?: string | null): string => {
  const params = new URLSearchParams();
  if (groupToken) {
    params.set('group', groupToken);
  }
  if (inviteId) {
    params.set('invite', inviteId);
  }
  if (gameId) {
    params.set('game', gameId);
  }
  const query = params.toString();
  return query ? `/online?${query}` : '/online';
};

export const buildRealShareLink = (gameId?: string | null): string => {
  const trimmed = (gameId || '').trim();
  if (!trimmed) return '';
  const origin = resolvePublicWebOrigin() || (typeof window !== 'undefined' ? window.location.origin : '');
  const normalized = origin.replace(/\/+$/, '');
  const encodedId = encodeURIComponent(trimmed);
  if (!normalized) return `/real/game/${encodedId}`;
  return `${normalized}/real/game/${encodedId}`;
};

export const buildFriendShareLink = (friendCode?: string | null): string => {
  const trimmed = (friendCode || '').trim();
  if (!trimmed) return '';
  const params = new URLSearchParams();
  params.set('friend', trimmed);
  const query = params.toString();
  const origin = resolvePublicWebOrigin() || (typeof window !== 'undefined' ? window.location.origin : '');
  const normalized = origin.replace(/\/+$/, '');
  if (!normalized) return `/account/social?${query}`;
  return `${normalized}/account/social?${query}`;
};

export const resolveAppUrlPath = (url: string): string | null => {
  try {
    const normalized = normalizeIncomingUrl(url);
    if (!normalized) return null;
    const parsed = new URL(normalized);
    if (parsed.protocol === `${APP_URL_SCHEME}:`) {
      const hostPath = parsed.host ? `/${parsed.host}` : '';
      const pathname = parsed.pathname === '/' ? '' : parsed.pathname;
      return `${hostPath}${pathname}${parsed.search}${parsed.hash}` || '/';
    }
    const isHttp = parsed.protocol === 'https:' || parsed.protocol === 'http:';
    const allowedOrigin = resolvePublicWebOrigin();
    const allowedOrigins = [allowedOrigin, DEFAULT_PUBLIC_WEB_ORIGIN].filter(Boolean);
    if (isHttp) {
      for (const origin of allowedOrigins) {
        const allowedHost = new URL(origin).host;
        if (parsed.host === allowedHost) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
        }
      }
    }
    return null;
  } catch {
    return null;
  }
};
